import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, fromEvent, merge, Subject, Subscription, timer } from 'rxjs';
import { switchMap, takeWhile, tap } from 'rxjs/operators';
import { HomeService } from 'src/app/modules/home/home.service';
import { LoggedInUserDetails } from 'src/app/models/LoggedInUserDetails';
import { UserMetadata } from 'src/app/models/UserMetadata';
import { environment } from 'src/environments/environment';
import { ExotelWebrtcService } from '../exotel-webrtc/exotel-webrtc.service';
import { StorageService } from '../storage/storage.service';

/** Seconds shown on the warning before auto-logout. */
const LOGOUT_COUNTDOWN_SECONDS = 10;
/** Shared across browser tabs (plain localStorage so `storage` events fire). */
const LAST_ACTIVITY_KEY = 'user_inactivity_last_activity_at';
/** Written after logout so other tabs can exit the session once (not on every clear). */
const LOGOUT_SIGNAL_KEY = 'user_inactivity_logout_signal';
/** Avoid writing localStorage on every mousemove. */
const ACTIVITY_BROADCAST_THROTTLE_MS = 1_000;

const ACTIVITY_EVENTS = [
  'click',
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'wheel',
] as const;

@Injectable({
  providedIn: 'root'
})
export class UserInactivityService implements OnDestroy {
  private readonly inactivityMs = environment.inactivityTimeoutMs;

  private watchSubscription: Subscription | null = null;
  private countdownSubscription: Subscription | null = null;
  private crossTabSubscription: Subscription | null = null;
  private userDetailsSubscription: Subscription | null = null;
  private isLoggingOut = false;
  private isWarningOpen = false;
  private lastBroadcastAt = 0;
  /** In-memory: this tab had a session when it started watching (survives other tabs clearing storage). */
  private sessionActive = false;

  /** Emits when another tab reports activity (resets this tab's idle timer). */
  private readonly crossTabActivitySubject = new Subject<void>();

  private readonly warningVisibleSubject = new BehaviorSubject<boolean>(false);
  private readonly secondsRemainingSubject = new BehaviorSubject<number>(LOGOUT_COUNTDOWN_SECONDS);

  readonly warningVisible$ = this.warningVisibleSubject.asObservable();
  readonly secondsRemaining$ = this.secondsRemainingSubject.asObservable();
  readonly inactivityDurationLabel = formatDuration(this.inactivityMs);

  constructor(
    private ngZone: NgZone,
    private homeService: HomeService,
    private storageService: StorageService,
    private router: Router,
    private exotelWebrtc: ExotelWebrtcService,
  ) {
    // Re-attempt start when user/get_details lands (login / client switch)
    this.userDetailsSubscription = this.homeService.userDetails.subscribe((details) => {
      const user = details as LoggedInUserDetails;
      if (user?.inactivity_enabled) {
        this.start();
      }
    });
  }

  /**
   * Start watching for user inactivity (safe to call more than once).
   * Single gate: only allowlisted users (per environment) are tracked.
   */
  start(): void {
    if (this.watchSubscription || this.isWarningOpen || this.isLoggingOut) {
      return;
    }

    if (!this.isLoggedIn() || !this.isInactivityEnabledForCurrentUser()) {
      return;
    }

    this.sessionActive = true;
    this.ensureCrossTabListener();
    this.broadcastActivity(true);

    this.ngZone.runOutsideAngular(() => {
      const localActivity$ = merge(
        ...ACTIVITY_EVENTS.map((eventName) =>
          fromEvent(document, eventName, { passive: true })
        )
      ).pipe(
        tap(() => this.broadcastActivity())
      );

      this.watchSubscription = merge(
        timer(0),
        localActivity$,
        this.crossTabActivitySubject
      )
        .pipe(switchMap(() => timer(this.inactivityMs)))
        .subscribe(() => {
          this.ngZone.run(() => this.handleInactivity());
        });
    });
  }

  stop(): void {
    this.watchSubscription?.unsubscribe();
    this.watchSubscription = null;
  }

  /** User chose to stay logged in — dismiss warning and restart idle watch. */
  stayLoggedIn(): void {
    this.broadcastActivity(true);
    this.resumeAfterWarning();
  }

  ngOnDestroy(): void {
    this.clearCountdown();
    this.stop();
    this.crossTabSubscription?.unsubscribe();
    this.crossTabSubscription = null;
    this.userDetailsSubscription?.unsubscribe();
    this.userDetailsSubscription = null;
  }

  private handleInactivity(): void {
    if (this.isLoggingOut || this.isWarningOpen || !this.isLoggedIn()) {
      return;
    }

    // Another tab may still be active — trust the shared timestamp
    const lastActivityAt = this.readLastActivityAt();
    if (lastActivityAt != null && Date.now() - lastActivityAt < this.inactivityMs) {
      this.crossTabActivitySubject.next();
      return;
    }

    this.stop();
    this.isWarningOpen = true;
    this.warningVisibleSubject.next(true);
    this.startLogoutCountdown();
  }

  private startLogoutCountdown(): void {
    this.clearCountdown();
    this.secondsRemainingSubject.next(LOGOUT_COUNTDOWN_SECONDS);

    this.countdownSubscription = timer(0, 1000)
      .pipe(takeWhile((elapsed) => elapsed <= LOGOUT_COUNTDOWN_SECONDS))
      .subscribe((elapsed) => {
        const remaining = LOGOUT_COUNTDOWN_SECONDS - elapsed;
        this.secondsRemainingSubject.next(remaining);

        if (remaining <= 0) {
          this.logoutDueToInactivity();
        }
      });
  }

  private clearCountdown(): void {
    this.countdownSubscription?.unsubscribe();
    this.countdownSubscription = null;
  }

  private resumeAfterWarning(): void {
    this.clearCountdown();
    this.isWarningOpen = false;
    this.warningVisibleSubject.next(false);
    this.secondsRemainingSubject.next(LOGOUT_COUNTDOWN_SECONDS);
    this.start();
  }

  private ensureCrossTabListener(): void {
    if (this.crossTabSubscription) {
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      this.crossTabSubscription = fromEvent<StorageEvent>(window, 'storage').subscribe((event) => {
        if (event.key === LAST_ACTIVITY_KEY && event.newValue) {
          this.ngZone.run(() => this.onCrossTabActivity());
          return;
        }

        if (event.key === LOGOUT_SIGNAL_KEY && event.newValue) {
          this.ngZone.run(() => this.onCrossTabLogout());
        }
      });
    });
  }

  private onCrossTabActivity(): void {
    if (this.isLoggingOut || !this.sessionActive) {
      return;
    }

    if (this.isWarningOpen) {
      this.resumeAfterWarning();
      return;
    }

    this.crossTabActivitySubject.next();
  }

  /**
   * Another tab logged out. Only tabs that still had a session should leave once.
   * Login-page tabs (sessionActive=false) must ignore this to avoid reload loops.
   */
  private onCrossTabLogout(): void {
    if (this.isLoggingOut || !this.sessionActive) {
      return;
    }

    this.sessionActive = false;
    this.isLoggingOut = true;
    this.clearCountdown();
    this.isWarningOpen = false;
    this.warningVisibleSubject.next(false);
    this.stop();

    if (!this.router.url.includes('/login')) {
      this.router.navigateByUrl('/login');
      location.reload();
    }
  }

  private broadcastActivity(force = false): void {
    const now = Date.now();
    if (!force && now - this.lastBroadcastAt < ACTIVITY_BROADCAST_THROTTLE_MS) {
      return;
    }

    this.lastBroadcastAt = now;
    try {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    } catch {
      // Ignore quota / private-mode write failures
    }
  }

  private broadcastLogoutSignal(): void {
    try {
      // Set after clear() so the signal is not wiped with the session
      localStorage.setItem(LOGOUT_SIGNAL_KEY, String(Date.now()));
    } catch {
      // Ignore quota / private-mode write failures
    }
  }

  private readLastActivityAt(): number | null {
    try {
      const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
      if (!raw) {
        return null;
      }
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  private isLoggedIn(): boolean {
    const userMetadata: UserMetadata = this.storageService.getItem('user_metadata');
    return !!(userMetadata && userMetadata.access_token);
  }

  /** True when user/get_details sets inactivity_enabled. */
  private isInactivityEnabledForCurrentUser(): boolean {
    const user = this.storageService.getItem('logged_in_user_details') as LoggedInUserDetails | null;
    return !!user?.inactivity_enabled;
  }

  private logoutDueToInactivity(): void {
    if (this.isLoggingOut) {
      return;
    }

    // Final cross-tab check before logging out
    const lastActivityAt = this.readLastActivityAt();
    if (lastActivityAt != null && Date.now() - lastActivityAt < this.inactivityMs) {
      this.resumeAfterWarning();
      return;
    }

    this.isLoggingOut = true;
    this.sessionActive = false;
    this.exotelWebrtc.releaseForLogout();
    this.clearCountdown();
    this.isWarningOpen = false;
    this.warningVisibleSubject.next(false);
    this.stop();

    this.homeService.logout({ reason: `inactivity since ${this.inactivityDurationLabel}` }).subscribe({
      next: () => this.clearSessionAndRedirect(),
      error: () => this.clearSessionAndRedirect(),
    });
  }

  private clearSessionAndRedirect(): void {
    this.storageService.clear();
    this.broadcastLogoutSignal();
    this.router.navigateByUrl('/login');
    location.reload();
  }
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds} ${totalSeconds === 1 ? 'second' : 'seconds'}`;
  }
  const minutes = Math.round(totalSeconds / 60);
  return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
}
